import { z } from 'zod'

// Base API Response Schema
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.any().nullable()
})

// Paginated Response Schema
export const PaginatedResponseSchema = z.object({
  totalPage: z.number(),
  totalElements: z.number(),
  totalOfAllElements: z.number(),
  data: z.array(z.any())
})

// User Schema
export const UserSchema = z.object({
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: z.string().email(),
  gender: z.enum(['MALE', 'FEMALE']).nullable(),
  phoneNumber: z.string(),
  profilePicture: z.string().nullable(),
  profileThemeColorCode: z.string().nullable()
})

// User Profile Schema (nested in AdminUser)
export const UserProfileSchema = z.object({
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: z.string().email()
})

// Admin User Schema - matches API response
export const AdminUserSchema = z.object({
  adminId: z.string(),
  userProfile: UserProfileSchema,
  userRole: z.string().optional(),
  isDefaultPassword: z.boolean().optional(),
  hasActivatedAccount: z.boolean().optional(),
  isTwoFactorEnabled: z.boolean().optional(),
  otpChannel: z.string().optional()
})

// Update Admin User Request Schema
export const UpdateAdminUserRequestSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1)
})

// Create/Invite Admin User Request Schema
export const CreateAdminUserRequestSchema = z.object({
  userDto: z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    emailAddress: z.string().email(),
    userRole: z.string()
  })
})

// Authentication Schemas
export const LoginRequestSchema = z.object({
  emailAddress: z.string().email(),
  password: z.string().min(6)
})

// User Profile Response (common structure)
export const UserProfileResponseSchema = z.object({
  userId: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  emailAddress: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  profilePicture: z.string().nullable().optional(),
  socialLinks: z.array(z.string()).optional(),
  isDefaultPassword: z.boolean().optional(),
  userRole: z.string().optional(),
  otpChannel: z.string().optional(),
  lastLoginWith2FA: z.string().optional(),
  hasActivatedAccount: z.boolean().optional(),
  isTwoFactorEnabled: z.boolean().optional()
})

export const LoginResponseSchema = z.object({
  token: z.string().optional(), // Only present when 2FA is enabled
  userProfileResponse: UserProfileResponseSchema
})


export const VerifyOtpRequestSchema = z.object({
  userId: z.string(),
  otp: z.string().length(6)
})


export const VerifyOtpResponseSchema = z.object({
  token: z.string(),
  userProfileResponse: UserProfileResponseSchema
})

// Verify Account Invite Request Schema
export const VerifyAccountInviteRequestSchema = z.object({
  userId: z.string(),
  otp: z.string()
})

// Activate Account Request Schema
export const ActivateAccountRequestSchema = z.object({
  userId: z.string(),
  password: z.string().min(8)
})

// Reset Password Request Schema (for reset password flow)
export const ResetPasswordRequestSchema = z.object({
  userId: z.string(),
  otp: z.string(),
  password: z.string().min(8)
})

// Content Document Schema (for ContentService - different from Documents module)
export const ContentDocumentSchema = z.object({
  documentId: z.string(),
  title: z.string(),
  content: z.string(),
  category: z.string(),
  status: z.enum(['DRAFT', 'PUBLISHED']),
  author: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
  tags: z.array(z.string()).optional(),
  featuredImage: z.string().nullable()
})

export const CreateContentDocumentSchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
  category: z.string(),
  status: z.enum(['DRAFT', 'PUBLISHED']).default('DRAFT'),
  tags: z.array(z.string()).optional(),
  featuredImage: z.string().optional()
})

export const UpdateContentDocumentSchema = CreateContentDocumentSchema.partial().extend({
  documentId: z.string()
})

// Event Schema
export const EventSchema = z.object({
  eventId: z.string(),
  imageUrl: z.string(),
  eventTitle: z.string(),
  eventVenue: z.string(),
  meetingLink: z.string().nullable().optional(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  description: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string(),
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  updatedBy: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional()
})

export const CreateEventSchema = z.object({
  eventTitle: z.string().min(1),
  eventVenue: z.string().optional(),
  meetingLink: z.string().nullable().optional(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  imageUrl: z.string().url(),
  description: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateEventSchema = z.object({
  eventTitle: z.string().min(1),
  eventVenue: z.string().optional(),
  meetingLink: z.string().nullable().optional(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  imageUrl: z.string().url(),
  description: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateEventStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const EventFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  searchBy: z.string().optional(),
  location: z.string().optional(),
  upcoming: z.boolean().optional(),
  past: z.boolean().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional(),
  eventStartDateStart: z.string().optional(),
  eventStartDateEnd: z.string().optional()
})

export const EventStatsSchema = z.object({
  totalEvents: z.number().default(0),
  publishedEvents: z.number().default(0),
  draftEvents: z.number().default(0),
  upcomingEvents: z.number().default(0),
  pastEvents: z.number().default(0),
  draftPercentageOfTotal: z.number().default(0),
  publishedPercentageOfTotal: z.number().default(0),
  upComingEvents: z.number().default(0),
  upcomingWindowDays: z.number().default(0)
})


// Tender Schema
export const TenderSchema = z.object({
  tenderId: z.string(),
  tenderTitle: z.string(),
  bidType: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  description: z.string(),
  tenderUrl: z.string().nullable(),
  submissionEmail: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  isExpired: z.boolean().nullable(),
  datePublished: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string(),
  tenderCategory: z.string().nullable(),
})

export const CreateTenderSchema = z.object({
  tenderTitle: z.string().min(1),
  bidType: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().min(1),
  tenderUrl: z.string().url().optional(),
  submissionEmail: z.string().email(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  tenderCategory: z.string().optional()
})

export const UpdateTenderSchema = z.object({
  tenderTitle: z.string().min(1),
  bidType: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().min(1),
  tenderUrl: z.string().url().optional(),
  submissionEmail: z.string().email(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  tenderCategory: z.string().optional()
})

export const TenderFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  tenderCategory: z.string().optional(),
  bidType: z.string().optional(),
  searchBy: z.string().optional(),
  closingSoon: z.boolean().optional(),
  isExpired: z.boolean().optional(),
  deadLineStartDate: z.string().optional(),
  deadLineEndDate: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

export const TenderStatsSchema = z.object({
  totalTender: z.number().default(0),
  publishedTender: z.number().default(0),
  publishedPercentageOfTotal: z.number().default(0),
  draftTender: z.number().default(0),
  draftPercentageOfTotal: z.number().default(0),
  closingSoonTender: z.number().default(0),
  closingSoonPercentageOfTotal: z.number().default(0),
  closedTender: z.number().default(0)
})

// Press Release Schema (for list/filter responses)
export const PressReleaseSchema = z.object({
  pressReleaseId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  summary: z.string().optional(),
  language: z.string().optional(),
  category: z.string().optional(),
  readTimeMinutes: z.number().optional(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  articleTag: z.string(),
  landingPage: z.boolean(),
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Press Release Detail Schema (for get by ID responses with audit fields)
export const PressReleaseDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string().optional(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  subtitle: z.string().optional(),
  summary: z.string().optional(),
  language: z.string().optional(),
  category: z.string().optional(),
  readTimeMinutes: z.number().optional(),
  coverImageUrl: z.string().optional(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  articleTag: z.string(),
  landingPage: z.boolean(),
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable()
})

// Press Release Filter Parameters Schema
export const PressReleaseFilterParamsSchema = z.object({
  title: z.string().optional(),
  articleTag: z.string().optional(),
  landingPage: z.boolean().optional(),
  pressRelease: z.boolean().optional(),
  pressReleaseBanner: z.boolean().optional(),
  allowComment: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// Press Release Stats Schema
export const PressReleaseStatsSchema = z.object({
  total: z.number().default(0),
  totalPublished: z.number().default(0),
  totalDrafts: z.number().default(0)
})

export const PressReleaseStatsResponseSchema = ApiResponseSchema.extend({
  data: PressReleaseStatsSchema
})

export const PressReleaseCategorySchema = z.object({
  name: z.string(),
  value: z.string()
})

// Create Press Release Schema
export const CreatePressReleaseSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().optional(),
  summary: z.string().optional(),
  language: z.string().min(1),
  coverImageTitle: z.string(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  articleTag: z.array(z.string()), // Array of tag strings
  category: z.string().min(1),
  readTimeMinutes: z.number().int().min(1),
  landingPage: z.boolean().optional(), // Now managed via WebPages section
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string() // Image URL string (required for create) - API expects this field name
})

// Update Press Release Schema
export const UpdatePressReleaseSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().optional(),
  summary: z.string().optional(),
  language: z.string().min(1),
  coverImageTitle: z.string(),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  articleTag: z.array(z.string()), // Array of tag strings
  category: z.string().min(1),
  readTimeMinutes: z.number().int().min(1),
  landingPage: z.boolean().optional(), // Now managed via WebPages section
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']),
  coverImageUrl: z.string() // Image URL string (required) - API expects this field name
})

// Press Release Form Schema (for react-hook-form validation)
export const PressReleaseFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional(),
  summary: z.string().optional(),
  language: z.string().min(1, 'Language is required'),
  category: z.string().min(1, 'Category is required'),
  readTimeMinutes: z.string().regex(/^\d+$/, 'Read time must be a number').min(1, 'Read time is required'),
  content: z.string().min(1, 'Content is required'),
  coverImageUrl: z.string().min(1, 'Cover image is required'),
  coverImageTitle: z.string().default(''),
  additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional(),
  articleTag: z.string().default(''), // Comma-separated string (will be parsed to array on submit)
  landingPage: z.boolean().default(false),
  pressRelease: z.boolean().default(true),
  pressReleaseBanner: z.boolean().default(false),
  allowComment: z.boolean().default(true),
})

// Website Page Schema
export const WebPageSchema = z.object({
  pageId: z.string(),
  title: z.string(),
  slug: z.string(),
  content: z.string(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  status: z.enum(['DRAFT', 'PUBLISHED']),
  template: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
})

// Update All Web Page Sections Schema
export const UpdateAllWebPageSectionsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']),
  contents: z.array(z.object({
    sectionTitle: z.string().nullable(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    contentType: z.string().nullable(),
    fileUrl: z.string().nullable(),
    emailAddress: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    phoneCountryCode: z.string().nullable(),
    address: z.string().nullable(),
    listOfPurpose: z.array(z.string()).nullable(),
    imageUrl: z.string().nullable(),
    sectionTag: z.string()
  }))
})

// Update Web Page Status Schema
export const UpdateWebPageStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const PaginatedWebPageSchema = z.object({
  websitePageId: z.string(),
  websitePageName: z.string(),
  updatedById: z.string().nullable(),
  updatedByName: z.string().nullable(),
  updatedAt: z.string()
})

export const PaginatedWebPageResponseSchema = ApiResponseSchema.extend({
  data: PaginatedResponseSchema.extend({
    data: z.array(PaginatedWebPageSchema)
  })
})

export const WebPagesStatsSchema = z.object({
  totalWebsitePages: z.number().default(0),
  totalPublishedPages: z.number().default(0),
  totalDraftPages: z.number().default(0),
  draftVsPublishedPercentageDifference: z.number().default(0),
  publishedVsDraftPercentageDifference: z.number().default(0)
})

export const WebPagesStatsResponseSchema = ApiResponseSchema.extend({
  data: WebPagesStatsSchema
})

export const DashboardStatBreakdownSchema = z.object({
  totalCount: z.number(),
  last30DaysCount: z.number(),
  previous30DaysCount: z.number(),
  percentageChange: z.number()
})

export const DashboardContentStatsSchema = z.object({
  comparisonWindowDays: z.number(),
  total: DashboardStatBreakdownSchema,
  published: DashboardStatBreakdownSchema,
  draft: DashboardStatBreakdownSchema
})

export const DashboardOperationalStatsSchema = z.object({
  comparisonWindowDays: z.number(),
  upcomingEvents: DashboardStatBreakdownSchema,
  openOpportunities: DashboardStatBreakdownSchema
})

export const DashboardContentStatsResponseSchema = ApiResponseSchema.extend({
  data: DashboardContentStatsSchema
})

export const DashboardOperationalStatsResponseSchema = ApiResponseSchema.extend({
  data: DashboardOperationalStatsSchema
})

export const DashboardContentPerformanceChartPointSchema = z.object({
  viewDate: z.string(),
  views: z.number()
})

export const DashboardTopContentPageSchema = z.object({
  name: z.string(),
  views: z.number()
})

export const DashboardContentPerformanceSchema = z.object({
  totalPageViews: z.number(),
  last30DaysPageViews: z.number(),
  previous30DaysPageViews: z.number(),
  percentageChange: z.number(),
  last7DaysChart: z.array(DashboardContentPerformanceChartPointSchema),
  top3ContentPages: z.array(DashboardTopContentPageSchema)
})

export const DashboardContentPerformanceResponseSchema = ApiResponseSchema.extend({
  data: DashboardContentPerformanceSchema
})

export const VersionSummarySchema = z.object({
  contentId: z.string(),
  versionNumber: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
  updatedByName: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string().nullable()
})

export const WebsitePageVersionInfoSchema = z.object({
  websitePageId: z.string(),
  websitePageName: z.string(),
  versionSummaries: z.array(VersionSummarySchema)
})

export const WebsitePageContentSchema = z.object({
  websitePageId: z.string(),
  websitePageName: z.string(),
  websitePageContentId: z.string(),
  versionNumber: z.string(),
  content: z.record(z.string(), z.any()), // Map of sectionTag to section data
  status: z.enum(['DRAFT', 'PUBLISH']),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string().nullable()
})

export const WebsitePageContentResponseSchema = ApiResponseSchema.extend({
  data: WebsitePageContentSchema
})

export const WebsitePageVersionsResponseSchema = ApiResponseSchema.extend({
  data: PaginatedResponseSchema.extend({
    data: z.array(WebsitePageVersionInfoSchema)
  })
})

export const WebPageVersionsStatsSchema = z.object({
  websitePageId: z.string(),
  totalVersions: z.number().default(0),
  draftCount: z.number().default(0),
  publishedCount: z.number().default(0),
  currentPublishedUpdatedAt: z.string().nullable().default(null)
})

export const WebPageVersionsStatsResponseSchema = ApiResponseSchema.extend({
  data: WebPageVersionsStatsSchema
})



// Region and Country Schemas
export const RegionSchema = z.object({
  regionId: z.string(),
  regionTag: z.string(),
  regionName: z.string(),
  regionDescription: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  updatedAt: z.string()
})

// Region Detail Schema (for detailed region response)
export const RegionDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  regionTag: z.string(),
  regionName: z.string(),
  regionDescription: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Member Country Region Schema (for member-countries/private/regions endpoint)
export const MemberCountryRegionSchema = z.object({
  regionId: z.string(),
  regionTag: z.string(),
  regionName: z.string(),
  numberOfCountries: z.number(),
  updatedAt: z.string()
})

export const RegionFilterParamsSchema = z.object({
  regionTag: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

export const UpdateRegionStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const CountrySchema = z.object({
  countryId: z.string(),
  countryName: z.string(),
  abbreviation: z.string(),
  flagUrl: z.string()
})

export const CountryFilterParamsSchema = z.object({
  regionTag: z.string().optional(),
  page: z.number().optional(),
  size: z.number().optional()
})

// Member Country Schemas
export const MemberCountrySchema = z.object({
  memberCountryId: z.string(),
  regionId: z.string(),
  regionName: z.string(),
  countryId: z.string(),
  countryName: z.string(),
  flagUrl: z.string(),
  title: z.string(),
  description: z.string(),
  officialLanguage: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  createdAt: z.string(),
  updatedAt: z.string(),
  dateJoined: z.string().nullable(),
  hasSpaceAgency: z.boolean().optional(),
  hasSpaceProgramme: z.boolean().optional(),
})

export const CreateMemberCountrySchema = z.object({
  regionId: z.string().min(1),
  countryId: z.string().min(1),
  isBio: z.boolean(),
  title: z.string().min(1),
  description: z.string().min(1),
  officialLanguage: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  dateJoined: z.string().optional()
})

export const UpdateMemberCountrySchema = z.object({
  regionId: z.string().min(1),
  countryId: z.string().min(1),
  isBio: z.boolean(),
  title: z.string().min(1),
  description: z.string().min(1),
  officialLanguage: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  dateJoined: z.string().optional()
})

export const MemberCountryFormSchema = z.object({
  countryId: z.string().trim().min(1, 'Country is required'),
  isBio: z.boolean().default(true),
  title: z.string().trim().default(''),
  description: z.string().trim().default(''),
  officialLanguage: z.string().trim().default(''),
  dateJoined: z.string().default(''),
  additionalLinks: z.url().trim().default('')
})

export const UpdateMemberCountryStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const MemberCountryFilterParamsSchema = z.object({
  regionId: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// Account Deletion Request Schema
export const AccountDeletionRequestSchema = z.object({
  userId: z.string(),
  reason: z.enum(['PRIVACY_CONCERNS', 'NO_LONGER_NEEDED', 'OTHERS']),
  deleteAt: z.string(),
  actorType: z.enum(['RESIDENT', 'ADMIN']),
  createdAt: z.string(),
  description: z.string(),
  user: UserSchema
})

// Strategic Pillar Schema (for list/filter responses)
export const StrategicPillarSchema = z.object({
  strategicPillarId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()),
  landingPagePrioritiesOfAfsa: z.boolean(),
  whoWeAre: z.boolean(),
  countries: z.boolean(),
  history: z.boolean(),
  member: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Strategic Pillar Detail Schema (for get by ID responses with audit fields)
export const StrategicPillarDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()),
  landingPagePrioritiesOfAfsa: z.boolean(),
  whoWeAre: z.boolean(),
  countries: z.boolean(),
  history: z.boolean(),
  member: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable()
})

// Strategic Pillar Filter Parameters Schema
export const StrategicPillarFilterParamsSchema = z.object({
  title: z.string().optional(),
  articleTag: z.string().optional(),
  landingPagePrioritiesOfAfsa: z.boolean().optional(),
  whoWeAre: z.boolean().optional(),
  countries: z.boolean().optional(),
  memberCountries: z.boolean().optional(),
  history: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional(),
  leadership: z.boolean().optional()
})

export const StrategicPillarStatsSchema = z.object({
  total: z.number().default(0),
  totalPublished: z.number().default(0),
  totalDrafts: z.number().default(0)
})

export const StrategicPillarStatsResponseSchema = ApiResponseSchema.extend({
  data: StrategicPillarStatsSchema
})

// Create Strategic Pillar Schema
export const CreateStrategicPillarSchema = z.object({
  title: z.string().min(1, 'Title is required'),
    subtitle: z.string().optional(),
  summary: z.string().optional(),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()),
  landingPagePrioritiesOfAfsa: z.boolean().optional(), // Now managed via WebPages section
  whoWeAre: z.boolean(),
  memberCountries: z.boolean(),
  history: z.boolean(),
  leadership: z.boolean(),
  content: z.string().min(1, 'Content is required'),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string(),
  language: z.string().min(1, 'Language is required')
})

// Update Strategic Pillar Schema
export const UpdateStrategicPillarSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  language: z.string().min(1, 'Language is required'),
   subtitle: z.string().optional(),
  summary: z.string().optional(),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()),
  landingPagePrioritiesOfAfsa: z.boolean().optional(), // Now managed via WebPages section
  whoWeAre: z.boolean(),
  memberCountries: z.boolean(),
  history: z.boolean(),
  leadership: z.boolean(),
  content: z.string().min(1, 'Content is required'),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().optional()
})

// Strategic Pillar Form Schema (for react-hook-form validation)
export const StrategicPillarFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string(),
  summary: z.string(),
  language: z.string().min(1, 'Language is required'),
  coverImageUrl: z.string().min(1, 'Cover image is required'),
  coverImageTitle: z.string().default(''),
  content: z.string().min(1, 'Content is required'),
  articleTag: z.string().default(''),
  landingPagePrioritiesOfAfsa: z.boolean().default(false),
  whoWeAre: z.boolean().default(false),
  memberCountries: z.boolean().default(false),
  history: z.boolean().default(false),
  leadership: z.boolean().default(false)
})

// Programme Schema (for list/filter responses)
export const ProgrammeSchema = z.object({
  programmeId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  coverImageUrl: z.string(),
  articleTag: z.array(z.string()),
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  websiteType: z.string().optional(),
})

export const MicroSiteRequestSchema = z.object({
  micrositeTemplateType: z.string(),
  branding: z.object({
    websiteLogoUrl: z.url(),
    websiteColorCode: z.string().optional(),
    textColorCode: z.string().optional()
  }).optional(),
  headerSection: z.object({
    headerImageUrl: z.preprocess(
      (val) => (val === "" ? null : val),
      z.string().url().nullable().optional()
    ),
    headerTitle: z.string().optional(),
    headerDescription: z.string().optional()
  }).optional(),
  headerSectionTwo: z.object({
    headerSectionTwoTitle: z.string().optional(),
    headerSectionTwoDescription: z.string().optional()
  }).optional(),
  missionTimeLineSection: z.object({
    missionTimeLineTitle: z.string().optional(),
    missionTimeLineDescription: z.string().optional(),
    missionTimeLineSectionDetails: z.array(z.object({
      missionTimeLineStartDate: z.string().optional(),
      missionTimeLineEndDate: z.string().optional(),
      missionTimeLineSectionTitle: z.string().optional(),
      missionTimeLineSectionDescription: z.string().optional(),
      missionTimeLineSectionImageUrl: z.preprocess(
        (val) => (val === "" ? null : val),
        z.string().url().nullable().optional()
      )
    })).optional().nullable()
  }).optional(),
  newsFeedSection: z.object({
    newsFeedTitle: z.string().optional(),
    newsFeedDescription: z.string().optional(),
    newsFeedSectionDetails: z.array(z.object({
      newsFeedSectionTitle: z.string().optional(),
      newsFeedSectionDescription: z.string().optional(),
      newsFeedSectionLink: z.preprocess(
        (val) => (val === "" ? null : val),
        z.string().url().nullable().optional()
      ),
      newsFeedSectionImageUrl: z.preprocess(
        (val) => (val === "" ? null : val),
        z.string().url().nullable().optional()
      )
    })).optional()
  }).optional(),
  footerSection: z.object({
    footerEmail: z.string().email().optional(),
    footerAddress: z.string().optional(),
    footerDescription: z.string().optional(),
    socialOne: z.url().nullable().optional(),
    socialTwo: z.url().nullable().optional(),
    socialThree: z.url().nullable().optional(),
    socialLinks: z.object({
      facebook: z.string().optional(),
      twitter: z.string().optional(),
      linkedIn: z.string().optional(),
      instagram: z.string().optional(),
      youtube: z.string().optional()
    }).optional()
  }).optional()
}).nullable()

// Programme Detail Schema (for get by ID responses with audit fields)
export const ProgrammeDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string().optional(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable(),
  totalViews: z.number().optional().default(0),
  microSite: MicroSiteRequestSchema.optional()
})



// Programme Filter Parameters Schema
export const ProgrammeFilterParamsSchema = z.object({
  title: z.string().optional(),
  articleTag: z.string().optional(),
  landingPageAfsaProgramme: z.boolean().optional(),
  allowComments: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

export const ProgrammeStatsSchema = z.object({
  total: z.number().default(0),
  totalPublished: z.number().default(0),
  totalDrafts: z.number().default(0)
})

export const ProgrammeStatsResponseSchema = ApiResponseSchema.extend({
  data: ProgrammeStatsSchema
})

// Create Programme Schema
export const CreateProgrammeSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()), // Array of strings
  landingPageAfsaProgramme: z.boolean().optional(), // Now managed via WebPages section
  allowComments: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string(), // API expects coverImageUrl
  websiteType: z.string().optional(),
  websiteLink: z.preprocess(val => (val === "" ? null : val), z.string().url().nullable().optional()),
  isAddWebsite: z.boolean().optional(),
  microSiteRequest: MicroSiteRequestSchema.nullable().optional()
})

// Update Programme Schema
export const UpdateProgrammeSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()), // Array of strings
  landingPageAfsaProgramme: z.boolean().optional(), // Now managed via WebPages section
  allowComments: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().optional(), // API expects coverImageUrl
  websiteType: z.string().optional(),
  websiteLink: z.preprocess(
    (val) => (val === "" ? null : val),
    z.string().url().nullable().optional()
  ),
  isAddWebsite: z.boolean().optional(),
  microSiteRequest: MicroSiteRequestSchema.nullable().optional()
})

const applyProgrammeWebsiteValidation = (
  data: {
    isAddWebsite?: boolean;
    websiteType?: string;
    websiteLink?: string | null;
  },
  ctx: z.RefinementCtx
) => {
  if (data.isAddWebsite && data.websiteType === 'WEBSITE_LINK') {
    if (!data.websiteLink) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['websiteLink'],
        message: 'Website link is required'
      })
      return;
    }

    try {
      new URL(data.websiteLink)
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['websiteLink'],
        message: 'Please provide a valid URL'
      })
    }
  }
}

// Programme Form Base Schema (safe for omit/pick/extend)
export const ProgrammeFormBaseSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required'),
  coverImageUrl: z.string().default(''),
  coverImageTitle: z.string().default(''),
  articleTag: z.string().default(''),
  landingPageAfsaProgramme: z.boolean().default(false),
  allowComments: z.boolean().default(true),
  status: z.string().default('DRAFT'),
  isAddWebsite: z.boolean().default(false),
  websiteType: z.string(),
  // websiteLink is optional if isAddWebsite is false, but required and must be a valid URL if isAddWebsite is true and websiteType is 'WEBSITE_LINK'.
  websiteLink: z.string().optional().nullable().default(''),
  microSiteRequest: MicroSiteRequestSchema.nullable().optional(),
  localCoverImageFile: z.instanceof(File).optional().nullable() // For handling local file uploads in the form, not sent to API
})

// Programme Form Schema (for react-hook-form validation)
export const ProgrammeFormSchema = ProgrammeFormBaseSchema.superRefine(applyProgrammeWebsiteValidation)

// Publication Schema (for list/filter responses)
export const PublicationSchema = z.object({
  publicationId: z.string(),
  title: z.string(),
  description: z.string(),
  publicationUrl: z.string(),
  coverImageUrl: z.string().nullable().optional(),
  publicationCategory: z.string(),
  externalLink: z.string().nullable().optional(),
  homePageKeyResources: z.boolean().nullable().optional(),
  isExternalSource: z.boolean().nullable().optional(),
  legalFoundation: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  totalViews: z.number().optional().default(0),
  totalDownloads: z.number().optional().default(0)
})

// Publication Filter Parameters Schema
export const PublicationFilterParamsSchema = z.object({
  publicationCategory: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  homePageKeyResources: z.boolean().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// Publication Stats Schema
export const PublicationStatsSchema = z.object({
  total: z.number(),
  totalPublished: z.number(),
  totalDrafts: z.number(),
  totalViews: z.number(),
  totalDownloads: z.number(),
  totalPercentageChange: z.number().optional().default(0),
  totalPublishedPercentageChange: z.number().optional().default(0),
  totalDraftsPercentageChange: z.number().optional().default(0),
  totalViewsPercentageChange: z.number().optional().default(0),
  totalDownloadsPercentageChange: z.number().optional().default(0)
})

// Create Publication Schema
export const CreatePublicationSchema = z.object({
  fileUrl: z.string().url().optional(),
  title: z.string().min(1),
  description: z.string().min(1),
  publicationCategory: z.string(),
  externalLink: z.string().url().nullable().optional(),
  homePageKeyResources: z.boolean().optional(),
  isExternalSource: z.boolean().optional(),
  legalFoundation: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string().nullable().optional()
})

// Update Publication Schema
export const UpdatePublicationSchema = z.object({
  fileUrl: z.string().url().optional(),
  title: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  publicationCategory: z.string().optional(),
  externalLink: z.string().url().nullable().optional(),
  homePageKeyResources: z.boolean().optional(),
  isExternalSource: z.boolean().optional(),
  legalFoundation: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().nullable().optional()
})

// Publication Form Schema (for react-hook-form validation)
export const PublicationFormSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  description: z.string().trim().min(1, 'Description is required'),
  publicationCategory: z.string().trim().min(1, 'Category is required'),
  sourceType: z.enum(['UPLOAD', 'EXTERNAL']).default('UPLOAD'),
  fileUrl: z.string().default(''),
  externalLink: z.string().default(''),
  homePageKeyResources: z.boolean().default(false),
  coverImageUrl: z.string().default(''),
}).superRefine((data, ctx) => {
  const plainDescription = data.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!plainDescription) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['description'],
      message: 'Description is required',
    });
  }

  if (data.sourceType === 'UPLOAD') {
    if (!data.fileUrl.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fileUrl'],
        message: 'Please upload a PDF file',
      });
    }
    return;
  }

  if (!data.externalLink.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['externalLink'],
      message: 'External link is required',
    });
    return;
  }

  const urlValidation = z.string().url().safeParse(data.externalLink.trim());
  if (!urlValidation.success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['externalLink'],
      message: 'Please provide a valid URL',
    });
  }
})

// LegalFoundation Schema (for list/filter responses)
export const LegalFoundationSchema = z.object({
  legalFoundationId: z.string(),
  title: z.string(),
  description: z.string(),
  legalFoundationUrl: z.string(),
  coverImageUrl: z.string().nullable().optional(),
  legalFoundationCategory: z.string(),
  externalLink: z.string().nullable().optional(),
  homePageKeyResources: z.boolean().nullable().optional(),
  isExternalSource: z.boolean().nullable().optional(),
  legalFoundation: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  totalViews: z.number().optional().default(0),
  totalDownloads: z.number().optional().default(0)
})

// LegalFoundation Filter Parameters Schema
export const LegalFoundationFilterParamsSchema = z.object({
  legalFoundationCategory: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  homePageKeyResources: z.boolean().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// LegalFoundation Stats Schema
export const LegalFoundationStatsSchema = z.object({
  total: z.number(),
  totalPublished: z.number(),
  totalDrafts: z.number(),
  totalViews: z.number(),
  totalDownloads: z.number(),
  totalPercentageChange: z.number().optional().default(0),
  totalPublishedPercentageChange: z.number().optional().default(0),
  totalDraftsPercentageChange: z.number().optional().default(0),
  totalViewsPercentageChange: z.number().optional().default(0),
  totalDownloadsPercentageChange: z.number().optional().default(0)
})

// Create LegalFoundation Schema
export const CreateLegalFoundationSchema = z.object({
  fileUrl: z.string().url().optional(),
  title: z.string().min(1),
  description: z.string().min(1),
  legalFoundationCategory: z.string(),
  externalLink: z.string().url().nullable().optional(),
  homePageKeyResources: z.boolean().optional(),
  isExternalSource: z.boolean().optional(),
  legalFoundation: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string().nullable().optional()
})

// Update LegalFoundation Schema
export const UpdateLegalFoundationSchema = z.object({
  fileUrl: z.string().url().optional(),
  title: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  legalFoundationCategory: z.string().optional(),
  externalLink: z.string().url().nullable().optional(),
  homePageKeyResources: z.boolean().optional(),
  isExternalSource: z.boolean().optional(),
  legalFoundation: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().nullable().optional()
})

// LegalFoundation Form Schema (for react-hook-form validation)
export const LegalFoundationFormSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  description: z.string().trim().min(1, 'Description is required'),
  legalFoundationCategory: z.string().trim().min(1, 'Category is required'),
  sourceType: z.enum(['UPLOAD', 'EXTERNAL']).default('UPLOAD'),
  fileUrl: z.string().default(''),
  externalLink: z.string().default(''),
  homePageKeyResources: z.boolean().default(false),
  coverImageUrl: z.string().default(''),
}).superRefine((data, ctx) => {
  const plainDescription = data.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!plainDescription) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['description'],
      message: 'Description is required',
    });
  }

  if (data.sourceType === 'UPLOAD') {
    if (!data.fileUrl.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fileUrl'],
        message: 'Please upload a PDF file',
      });
    }
    return;
  }

  if (!data.externalLink.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['externalLink'],
      message: 'External link is required',
    });
    return;
  }

  const urlValidation = z.string().url().safeParse(data.externalLink.trim());
  if (!urlValidation.success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['externalLink'],
      message: 'Please provide a valid URL',
    });
  }
})

// Query Parameters Schema
export const PaginationParamsSchema = z.object({
  page: z.number().min(1).default(1),
  size: z.number().min(1).max(100).default(10)
})

export const SearchParamsSchema = z.object({
  searchBy: z.string().optional(),
  category: z.string().optional(),
  status: z.string().optional()
})

// Export types
export type ApiResponse<T = unknown> = z.infer<typeof ApiResponseSchema> & { data: T }
export type PaginatedResponse<T = unknown> = z.infer<typeof PaginatedResponseSchema> & { data: T[] }

export type User = z.infer<typeof UserSchema>
export type AdminUser = z.infer<typeof AdminUserSchema>
export type LoginRequest = z.infer<typeof LoginRequestSchema>
export type LoginResponse = z.infer<typeof LoginResponseSchema>
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>
export type VerifyOtpRequest = z.infer<typeof VerifyOtpRequestSchema>
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>
export type CreateAdminUserRequest = z.infer<typeof CreateAdminUserRequestSchema>
export type VerifyAccountInviteRequest = z.infer<typeof VerifyAccountInviteRequestSchema>
export type ActivateAccountRequest = z.infer<typeof ActivateAccountRequestSchema>
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>

export type ContentDocument = z.infer<typeof ContentDocumentSchema>
export type CreateContentDocument = z.infer<typeof CreateContentDocumentSchema>
export type UpdateContentDocument = z.infer<typeof UpdateContentDocumentSchema>

export type Event = z.infer<typeof EventSchema>
export type CreateEvent = z.infer<typeof CreateEventSchema>
export type UpdateEvent = z.infer<typeof UpdateEventSchema>
export type UpdateEventStatus = z.infer<typeof UpdateEventStatusSchema>
export type EventFilterParams = z.infer<typeof EventFilterParamsSchema>
export type EventStats = z.infer<typeof EventStatsSchema>

export type Tender = z.infer<typeof TenderSchema>
export type CreateTender = z.infer<typeof CreateTenderSchema>
export type UpdateTender = z.infer<typeof UpdateTenderSchema>
export type TenderFilterParams = z.infer<typeof TenderFilterParamsSchema>
export type TenderStats = z.infer<typeof TenderStatsSchema>

export type PressRelease = z.infer<typeof PressReleaseSchema>
export type PressReleaseDetail = z.infer<typeof PressReleaseDetailSchema>
export type PressReleaseFilterParams = z.infer<typeof PressReleaseFilterParamsSchema>
export type PressReleaseStats = z.infer<typeof PressReleaseStatsSchema>
export type PressReleaseStatsResponse = z.infer<typeof PressReleaseStatsResponseSchema>
export type PressReleaseCategory = z.infer<typeof PressReleaseCategorySchema>
export type CreatePressRelease = z.infer<typeof CreatePressReleaseSchema>
export type UpdatePressRelease = z.infer<typeof UpdatePressReleaseSchema>
export type PressReleaseFormData = z.infer<typeof PressReleaseFormSchema>

export type StrategicPillar = z.infer<typeof StrategicPillarSchema>
export type StrategicPillarDetail = z.infer<typeof StrategicPillarDetailSchema>
export type StrategicPillarFilterParams = z.infer<typeof StrategicPillarFilterParamsSchema>
export type StrategicPillarStats = z.infer<typeof StrategicPillarStatsSchema>
export type StrategicPillarStatsResponse = z.infer<typeof StrategicPillarStatsResponseSchema>
export type CreateStrategicPillar = z.infer<typeof CreateStrategicPillarSchema>
export type UpdateStrategicPillar = z.infer<typeof UpdateStrategicPillarSchema>
export type StrategicPillarFormData = z.infer<typeof StrategicPillarFormSchema>

export type Programme = z.infer<typeof ProgrammeSchema>
export type ProgrammeDetail = z.infer<typeof ProgrammeDetailSchema>
export type ProgrammeFilterParams = z.infer<typeof ProgrammeFilterParamsSchema>
export type ProgrammeStats = z.infer<typeof ProgrammeStatsSchema>
export type ProgrammeStatsResponse = z.infer<typeof ProgrammeStatsResponseSchema>
export type CreateProgramme = z.infer<typeof CreateProgrammeSchema>
export type UpdateProgramme = z.infer<typeof UpdateProgrammeSchema>
export type ProgrammeFormData = z.infer<typeof ProgrammeFormSchema>

export const PublicationCategorySchema = z.object({
  displayName: z.string(),
  value: z.string()
})

export type Publication = z.infer<typeof PublicationSchema>
export type PublicationCategory = z.infer<typeof PublicationCategorySchema>
export type PublicationFilterParams = z.infer<typeof PublicationFilterParamsSchema>
export type PublicationStats = z.infer<typeof PublicationStatsSchema>
export type CreatePublication = z.infer<typeof CreatePublicationSchema>
export type UpdatePublication = z.infer<typeof UpdatePublicationSchema>
export type PublicationFormData = z.infer<typeof PublicationFormSchema>

export const LegalFoundationCategorySchema = z.object({
  displayName: z.string(),
  value: z.string()
})

export type LegalFoundation = z.infer<typeof LegalFoundationSchema>
export type LegalFoundationCategory = z.infer<typeof LegalFoundationCategorySchema>
export type LegalFoundationFilterParams = z.infer<typeof LegalFoundationFilterParamsSchema>
export type LegalFoundationStats = z.infer<typeof LegalFoundationStatsSchema>
export type CreateLegalFoundation = z.infer<typeof CreateLegalFoundationSchema>
export type UpdateLegalFoundation = z.infer<typeof UpdateLegalFoundationSchema>
export type LegalFoundationFormData = z.infer<typeof LegalFoundationFormSchema>

// Folder Schemas
export const FolderSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  folderName: z.string(),
  folderType: z.enum(['VIDEO', 'PHOTO']).optional()
})

export const FolderListItemSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  folderType: z.enum(['VIDEO', 'PHOTO']).optional(),
  totalItems: z.number().optional(),
  totalAlbums: z.number().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  previewUrls: z.array(z.string().url()).optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  updatedBy: z.string().nullable(),
  updatedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export const PhotoGalleryStorageStatsSchema = z.object({
  used: z.number().default(0),
  total: z.number().default(0),
})

export const PhotoGalleryStatsSchema = z.object({
  photoFolders: z.number().default(0),
  publishedPhotos: z.number().default(0),
  draftPhotos: z.number().default(0),
  totalPhotos: z.number().default(0),
  storage: PhotoGalleryStorageStatsSchema.default({ used: 0, total: 0 }),
})

export const CreateFolderSchema = z.object({
  folderName: z.string().min(1),
  folderType: z.enum(['VIDEO', 'PHOTO'])
})

export const UpdateFolderSchema = z.object({
  folderName: z.string().min(1),
  folderType: z.enum(['VIDEO', 'PHOTO']).optional()
})

export const FolderSearchParamsSchema = z.object({
  searchBy: z.string().optional(),
  size: z.number().min(1).default(10),
  page: z.number().min(1).default(1),
  folderType: z.enum(['VIDEO', 'PHOTO']).optional()
})

export type Folder = z.infer<typeof FolderSchema>
export type FolderListItem = z.infer<typeof FolderListItemSchema>
export type CreateFolder = z.infer<typeof CreateFolderSchema>
export type UpdateFolder = z.infer<typeof UpdateFolderSchema>
export type FolderSearchParams = z.infer<typeof FolderSearchParamsSchema>
export type PhotoGalleryStats = z.infer<typeof PhotoGalleryStatsSchema>

// Gallery Schemas
export const PhotoDetailSchema = z.object({
  comment: z.string(),
  photoUrl: z.string()
})

export const GallerySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  folderId: z.string(),
  title: z.string(),
  photoDetails: z.array(PhotoDetailSchema),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string()
})

export const GalleryTitleResponseSchema = z.object({
  photoGalleryId: z.string(),
  title: z.string(),
  dateCreated: z.string(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string(),
  photoUrl: z.string().nullable(),
  datePublished: z.string().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string()
})

export const GalleryFolderResponseSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  titleResponseList: z.array(GalleryTitleResponseSchema)
})

export const GalleryDetailSchema = z.object({
  galleryId: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  title: z.string(),
  photoDetails: z.array(PhotoDetailSchema),
  description: z.string().nullable(),
  altText: z.string().nullable(),
  caption: z.string().nullable(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string(),
  datePublished: z.string(),
  createdAt: z.string()
})

export const CreateGallerySchema = z.object({
  folderId: z.string(),
  title: z.string().min(1),
  photoDetails: z.array(z.object({
    comment: z.string(),
    photoUrl: z.string().url()
  })),
  isKeyHighlight: z.boolean().optional(), // Now managed via WebPages section
  isMultimedia: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateGallerySchema = z.object({
  folderId: z.string().optional(),
  title: z.string().min(1).optional(),
  isKeyHighlight: z.boolean().optional(),
  isMultimedia: z.boolean().optional()
})

export const UpdateGalleryStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const GalleryStatsSchema = z.object({
  total: z.number(),
  published: z.number(),
  draft: z.number()
})

export type PhotoDetail = z.infer<typeof PhotoDetailSchema>
export type Gallery = z.infer<typeof GallerySchema>
export type GalleryTitleResponse = z.infer<typeof GalleryTitleResponseSchema>
export type GalleryFolderResponse = z.infer<typeof GalleryFolderResponseSchema>
export type GalleryDetail = z.infer<typeof GalleryDetailSchema>
export type CreateGallery = z.infer<typeof CreateGallerySchema>
export type UpdateGallery = z.infer<typeof UpdateGallerySchema>
export type UpdateGalleryStatus = z.infer<typeof UpdateGalleryStatusSchema>
export type GalleryStats = z.infer<typeof GalleryStatsSchema>

export const GalleryHighlightSchema = z.object({
  galleryId: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  title: z.string(),
  photoUrl: z.string(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string(),
  datePublished: z.string().nullable(),
  createdAt: z.string(),
  publishedBy: z.string(),
  publishedByName: z.string()
})

export type GalleryHighlight = z.infer<typeof GalleryHighlightSchema>

// Video Schemas
export const VideoSchema = z.object({
  videoUploadId: z.string(),
  folderId: z.string().optional(),
  folderName: z.string().optional(),
  title: z.string(),
  embeddedLink: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  description: z.string().nullable(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

export const CreateVideoSchema = z.object({
  folderId: z.string(),
  title: z.string().min(1),
  embeddedLink: z.string().url(),
  thumbnailUrl: z.string().url().optional(),
  description: z.string().optional(),
  isKeyHighlight: z.boolean().optional(),
  isMultimedia: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateVideoSchema = z.object({
  folderId: z.string().optional(),
  title: z.string().min(1).optional(),
  embeddedLink: z.string().url().optional(),
  thumbnailUrl: z.string().url().optional(),
  description: z.string().optional(),
  isKeyHighlight: z.boolean().optional(),
  isMultimedia: z.boolean().optional()
})

export const UpdateVideoStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const VideoFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  folderId: z.string().optional()
})

export const VideoStatsSchema = z.object({
  total: z.number(),
  published: z.number(),
  draft: z.number()
})

export const VideoStorageStatsSchema = z.object({
  used: z.number().default(0),
  total: z.number().default(0),
})

export const VideoDashboardStatsSchema = z.object({
  videoFolders: z.number().default(0),
  publishedVideo: z.number().default(0),
  draftPhotos: z.number().default(0),
  totalPhotos: z.number().default(0),
  storage: VideoStorageStatsSchema.default({ used: 0, total: 0 }),
})

// Video item within a folder response
export const VideoItemSchema = z.object({
  videoUploadId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  embeddedLink: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  isKeyHighlight: z.boolean().nullable().optional(),
  isMultimedia: z.boolean().nullable().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().optional(),
  createdByName: z.string().optional(),
  publishedBy: z.string().nullable().optional(),
  publishedByName: z.string().nullable().optional()
})

// Folder with videos response (from filter endpoint)
export const VideoFolderResponseSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  responseList: z.array(VideoItemSchema)
})

// Paginated response for video folders
export const VideoFilterResponseSchema = z.object({
  totalPage: z.number(),
  totalElements: z.number(),
  totalOfAllElements: z.number(),
  data: z.array(VideoFolderResponseSchema)
})

export type Video = z.infer<typeof VideoSchema>
export type VideoItem = z.infer<typeof VideoItemSchema>
export type VideoFolderResponse = z.infer<typeof VideoFolderResponseSchema>
export type VideoFilterResponse = z.infer<typeof VideoFilterResponseSchema>
export type CreateVideo = z.infer<typeof CreateVideoSchema>
export type UpdateVideo = z.infer<typeof UpdateVideoSchema>
export type UpdateVideoStatus = z.infer<typeof UpdateVideoStatusSchema>
export type VideoFilterParams = z.infer<typeof VideoFilterParamsSchema>
export type VideoStats = z.infer<typeof VideoStatsSchema>
export type VideoDashboardStats = z.infer<typeof VideoDashboardStatsSchema>

// Member Schemas
export const MemberSchema = z.object({
  memberId: z.string(),
  fullName: z.string(),
  countryId: z.string(),
  countryName: z.string(),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER', 'DIRECTOR', 'SECRETARY']),
  imageUrl: z.string(),
  bio: z.string(),
  socialMediaLinks: z.array(z.string()).optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).nullable(),
  datePublished: z.string().nullable(),
  updatedAt: z.string().optional(),
  createdBy: z.string(),
  createdUsername: z.string()
})

export const CreateMemberSchema = z.object({
  fullName: z.string().min(1),
  countryId: z.string().min(1),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER', 'DIRECTOR', 'SECRETARY']),
  imageUrl: z.string().url(),
  isBio: z.boolean(),
  bio: z.string().min(1),
  socialMediaLinks: z.array(z.string().url()).optional(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateMemberSchema = z.object({
  fullName: z.string().min(1),
  countryId: z.string().min(1),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER', 'DIRECTOR', 'SECRETARY']),
  imageUrl: z.string().url(),
  isBio: z.boolean(),
  bio: z.string().min(1),
  socialMediaLinks: z.array(z.string().url()).optional(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Member Form Schema (for react-hook-form validation)
export const MemberSocialLinkSchema = z.object({
  type: z.enum(['WEBSITE', 'LINKEDIN', 'X_TWITTER', 'FACEBOOK', 'INSTAGRAM', 'YOUTUBE', 'EMAIL']),
  value: z.string().trim().min(1, 'Link value is required'),
})

export const MemberFormSchema = z.object({
  fullName: z.string().trim().min(1, 'Full name is required'),
  countryId: z.string().trim().min(1, 'Country is required'),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER', 'DIRECTOR', 'SECRETARY'], {
    message: 'Category is required',
  }),
  isBio: z.boolean().default(true),
  bio: z.string().default(''),
  socialMediaLinks: z.array(MemberSocialLinkSchema).default([]),
  imageUrl: z.string().trim().min(1, 'Profile photo is required'),
}).superRefine((data, ctx) => {
  if (data.isBio) {
    const plainBio = data.bio.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!plainBio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bio'],
        message: 'Biography is required when Add Bio is enabled',
      });
    }
  }

  data.socialMediaLinks.forEach((link, index) => {
    const value = link.value.trim();
    if (!value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['socialMediaLinks', index, 'value'],
        message: 'Link value is required',
      });
      return;
    }

    if (link.type === 'EMAIL') {
      const emailValue = value.replace(/^mailto:/i, '').trim();
      const emailValid = z.string().email().safeParse(emailValue);
      if (!emailValid.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['socialMediaLinks', index, 'value'],
          message: 'Enter a valid email address',
        });
      }
      return;
    }

    const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    const urlValid = z.string().url().safeParse(normalized);
    if (!urlValid.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['socialMediaLinks', index, 'value'],
        message: 'Enter a valid URL',
      });
    }
  })
})

export const UpdateMemberStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const MemberFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER', 'DIRECTOR', 'SECRETARY']).optional().nullable(),
  countryId: z.string().optional().nullable(),
  searchBy: z.string().optional().nullable(),
  createdStartDate: z.string().optional().nullable(),
  createdEndDate: z.string().optional().nullable(),
  publishedStartDate: z.string().optional().nullable(),
  publishedEndDate: z.string().optional().nullable()
})

// Member Category Schema
export const MemberCategorySchema = z.object({
  value: z.string(),
  displayName: z.string()
})

export const MemberStatsSchema = z.object({
  totalMembers: z.number().default(0),
  totalDraft: z.number().default(0),
  totalPublished: z.number().default(0),
  totalPresidentMembers: z.number().default(0)
})

export const MemberStatsResponseSchema = ApiResponseSchema.extend({
  data: MemberStatsSchema
})

export const MembersCategoryCountSchema = z.object({
    published: {
      president: z.number().default(0),
      vicePresident: z.number().default(0),
      member: z.number().default(0),
      total: z.number().default(0)
    },
    draft: {
      president: z.number().default(0),
      vicePresident: z.number().default(0),
      member: z.number().default(0),
      total: z.number().default(0)
    }
})

export type Member = z.infer<typeof MemberSchema>
export type CreateMember = z.infer<typeof CreateMemberSchema>
export type UpdateMember = z.infer<typeof UpdateMemberSchema>
export type MemberFormData = z.infer<typeof MemberFormSchema>
export type UpdateMemberStatus = z.infer<typeof UpdateMemberStatusSchema>
export type MemberFilterParams = z.infer<typeof MemberFilterParamsSchema>
export type MemberCategory = z.infer<typeof MemberCategorySchema>
export type MemberStats = z.infer<typeof MemberStatsSchema>
export type MemberStatsResponse = z.infer<typeof MemberStatsResponseSchema>
export type MembersCategoryCount = z.infer<typeof MembersCategoryCountSchema>

export type WebPage = z.infer<typeof WebPageSchema>
export type PaginatedWebPage = z.infer<typeof PaginatedWebPageSchema>
export type PaginatedWebPageResponse = z.infer<typeof PaginatedWebPageResponseSchema>
export type WebPagesStats = z.infer<typeof WebPagesStatsSchema>
export type WebPagesStatsResponse = z.infer<typeof WebPagesStatsResponseSchema>
export type DashboardStatBreakdown = z.infer<typeof DashboardStatBreakdownSchema>
export type DashboardContentStats = z.infer<typeof DashboardContentStatsSchema>
export type DashboardOperationalStats = z.infer<typeof DashboardOperationalStatsSchema>
export type DashboardContentStatsResponse = z.infer<typeof DashboardContentStatsResponseSchema>
export type DashboardOperationalStatsResponse = z.infer<typeof DashboardOperationalStatsResponseSchema>
export type DashboardContentPerformanceChartPoint = z.infer<typeof DashboardContentPerformanceChartPointSchema>
export type DashboardTopContentPage = z.infer<typeof DashboardTopContentPageSchema>
export type DashboardContentPerformance = z.infer<typeof DashboardContentPerformanceSchema>
export type DashboardContentPerformanceResponse = z.infer<typeof DashboardContentPerformanceResponseSchema>
export type VersionSummary = z.infer<typeof VersionSummarySchema>
export type WebsitePageVersionInfo = z.infer<typeof WebsitePageVersionInfoSchema>
export type WebsitePageContent = z.infer<typeof WebsitePageContentSchema>
export type WebsitePageContentResponse = z.infer<typeof WebsitePageContentResponseSchema>
export type WebsitePageVersionsResponse = z.infer<typeof WebsitePageVersionsResponseSchema>
export type WebPageVersionsStats = z.infer<typeof WebPageVersionsStatsSchema>
export type WebPageVersionsStatsResponse = z.infer<typeof WebPageVersionsStatsResponseSchema>
export type UpdateAllWebPageSections = z.infer<typeof UpdateAllWebPageSectionsSchema>
export type UpdateWebPageStatus = z.infer<typeof UpdateWebPageStatusSchema>

export type Region = z.infer<typeof RegionSchema>
export type RegionDetail = z.infer<typeof RegionDetailSchema>
export type MemberCountryRegion = z.infer<typeof MemberCountryRegionSchema>
export type RegionFilterParams = z.infer<typeof RegionFilterParamsSchema>
export type UpdateRegionStatus = z.infer<typeof UpdateRegionStatusSchema>
export type Country = z.infer<typeof CountrySchema>
export type CountryFilterParams = z.infer<typeof CountryFilterParamsSchema>
export const MemberCountryStatsSchema = z.object({
  totalRegions: z.number().default(0),
  totalCountries: z.number().default(0),
  publishedCountries: z.number().default(0),
  draftCountries: z.number().default(0),
  latestUpdatedAt: z.string().nullable()
})

export const MemberCountryStatsResponseSchema = ApiResponseSchema.extend({
  data: MemberCountryStatsSchema
})

export const MemberCountryRegionStatsSchema = z.object({
  regionId: z.string(),
  regionTag: z.string(),
  regionName: z.string(),
  totalCountries: z.number().default(0),
  publishedCountries: z.number().default(0),
  draftCountries: z.number().default(0),
  latestUpdatedAt: z.string().nullable()
})

export const MemberCountryRegionStatsResponseSchema = ApiResponseSchema.extend({
  data: MemberCountryRegionStatsSchema
})

export type MemberCountry = z.infer<typeof MemberCountrySchema>
export type CreateMemberCountry = z.infer<typeof CreateMemberCountrySchema>
export type UpdateMemberCountry = z.infer<typeof UpdateMemberCountrySchema>
export type UpdateMemberCountryStatus = z.infer<typeof UpdateMemberCountryStatusSchema>
export type MemberCountryFilterParams = z.infer<typeof MemberCountryFilterParamsSchema>
export type MemberCountryStats = z.infer<typeof MemberCountryStatsSchema>
export type MemberCountryStatsResponse = z.infer<typeof MemberCountryStatsResponseSchema>
export type MemberCountryRegionStats = z.infer<typeof MemberCountryRegionStatsSchema>
export type MemberCountryRegionStatsResponse = z.infer<typeof MemberCountryRegionStatsResponseSchema>
export type AccountDeletionRequest = z.infer<typeof AccountDeletionRequestSchema>

export type PaginationParams = z.infer<typeof PaginationParamsSchema>
export type SearchParams = z.infer<typeof SearchParamsSchema>

// History Schemas
export const HistorySchema = z.object({
  historyId: z.string(),
  imageUrl: z.string(),
  year: z.number(),
  title: z.string(),
  description: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUserName: z.string()
})

export const CreateHistorySchema = z.object({
  fileUrl: z.string().url(),
  year: z.number().min(1900).max(2100),
  title: z.string().min(1),
  description: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateHistorySchema = z.object({
  fileUrl: z.string().url(),
  year: z.number().min(1900).max(2100),
  title: z.string().min(1),
  description: z.string().min(1)
})

export const HistoryFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  year: z.number().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

export const UpdateHistoryStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const HistoryStatsSchema = z.object({
        comparisonWindowDays: z.number().default(30),
        totalHistories: z.object({
          totalCount: z.number().default(0),
          last30DaysCount: z.number().default(0),
          previous30DaysCount: z.number().default(0),
          percentageChange: z.number().default(0)
        }),
        publishedHistories: z.object({
          totalCount: z.number().default(0),
          last30DaysCount: z.number().default(0),
          previous30DaysCount: z.number().default(0),
          percentageChange: z.number().default(0)
        }),
        draftHistories: z.object({
          totalCount: z.number().default(0),
          last30DaysCount: z.number().default(0),
          previous30DaysCount: z.number().default(0),
          percentageChange: z.number().default(0)
        }),
        latestUpdatedAt: z.string().nullable(),
        latestUpdatedBy: z.string().nullable(),
        latestUpdatedByName: z.string().nullable()
})

export type History = z.infer<typeof HistorySchema>
export type CreateHistory = z.infer<typeof CreateHistorySchema>
export type UpdateHistory = z.infer<typeof UpdateHistorySchema>
export type HistoryFilterParams = z.infer<typeof HistoryFilterParamsSchema>
export type UpdateHistoryStatus = z.infer<typeof UpdateHistoryStatusSchema>
export type HistoryStats = z.infer<typeof HistoryStatsSchema>

// Job Schemas
export const JobSchema = z.object({
  jobId: z.string(),
  jobTitle: z.string(),
  jobLocation: z.string(),
  jobType: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  description: z.string().nullable(),
  submissionEmail: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  isExpired: z.boolean().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string(),
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  totalViews: z.number().nullable()
})

export const CreateJobSchema = z.object({
  jobTitle: z.string().min(1),
  jobLocation: z.string().min(1),
  jobType: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().min(1),
  // submissionEmail: z.string().email(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateJobSchema = z.object({
  jobTitle: z.string().min(1),
  jobLocation: z.string().min(1),
  jobType: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().min(1),
  // submissionEmail: z.string().email(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateJobStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const JobFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  jobType: z.string().optional(),
  searchBy: z.string().optional(),
  isExpired: z.boolean().optional(),
  deadLineStartDate: z.string().optional(),
  deadLineEndDate: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()

})

export const JobStatsSchema = z.object({
  total: z.number(),
  totalPublished: z.number(),
  totalDrafts: z.number(),
  totalEndingSoon: z.number(),
  totalClosed: z.number()
})

export type Job = z.infer<typeof JobSchema>
export type CreateJob = z.infer<typeof CreateJobSchema>
export type UpdateJob = z.infer<typeof UpdateJobSchema>
export type UpdateJobStatus = z.infer<typeof UpdateJobStatusSchema>
export type JobFilterParams = z.infer<typeof JobFilterParamsSchema>
export type JobStats = z.infer<typeof JobStatsSchema>

// Application Schemas
export const ApplicationSchema = z.object({  
    applicationId: z.string(),
jobId: z.string(),
jobTitle: z.string(),
cvUrl: z.string().url(),
coverLetterUrl: z.string().url().optional(),
education: z.array(z.object({
    schoolName: z.string(),
    degree: z.string(),
    discipline: z.string(),
    optionGrade: z.string().optional(),
    startYear: z.number().int(),
    endYear: z.number().int().optional(),
    currentlyStudying: z.boolean().default(false),
    location: z.string().optional(),
    description: z.string().optional()
})).optional(),
workExperience: z.array(z.object({
    companyName: z.string(),
    role: z.string(),    
    startYear: z.number().int(),
    endYear: z.number().int().optional(),
    currentlyWorking: z.boolean().default(false),
    location: z.string().optional(),
    description: z.string().optional(),
    responsibilities: z.array(z.string()).optional(),
    achievements: z.array(z.string()).optional()
})).optional(),
email: z.string().email(),
firstName: z.string(),
lastName: z.string(),
country: z.string(),
phoneNumber: z.string().optional(),
linkedIn: z.string().url().optional(),
portfolio: z.string().url().optional(),
status: z.enum(['NEW', 'REVIEW', 'SHORTLISTED', 'INTERVIEW', 'OFFERED', 'HIRED', 'REJECTED', 'WITHDRAWN', 'ON_HOLD']),
createdAt: z.string(),
updatedAt: z.string().optional(),
createdBy: z.string().nullable().optional(),
updatedBy: z.string().nullable().optional()

})

export const CreateApplicationSchema = z.object({
  applicationTitle: z.string().min(1),
  applicationLocation: z.string().min(1),
  applicationType: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().min(1),
  submissionEmail: z.string().email(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateApplicationSchema = z.object({
  applicationTitle: z.string().min(1),
  applicationLocation: z.string().min(1),
  applicationType: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  description: z.string().min(1),
  submissionEmail: z.string().email(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateApplicationStatusSchema = z.object({
 status: z.enum(['NEW', 'REVIEW', 'SHORTLISTED', 'INTERVIEW', 'OFFERED', 'HIRED', 'REJECTED', 'WITHDRAWN', 'ON_HOLD']),
})

export const ApplicationFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

export const ApplicationStatsSchema = z.object({
  totalApplications: z.number().default(0),
  last30DaysApplications: z.number().default(0),
  previous30DaysApplications: z.number().default(0),
  totalApplicationsPercentageChange: z.number().default(0),
  newApplications: z.number().default(0),
  newApplicationsPercentageOfTotal: z.number().default(0),
  reviewApplications: z.number().default(0),
  reviewApplicationsPercentageOfTotal: z.number().default(0),
  interviewApplications: z.number().default(0),
  interviewApplicationsPercentageOfTotal: z.number().default(0),
  rejectedApplications: z.number().default(0),
  rejectedApplicationsPercentageOfTotal: z.number().default(0),
  hiredApplications: z.number().default(0),
  hiredApplicationsPercentageOfTotal: z.number().default(0),
})

export type Application = z.infer<typeof ApplicationSchema>
export type CreateApplication = z.infer<typeof CreateApplicationSchema>
export type UpdateApplication = z.infer<typeof UpdateApplicationSchema>
export type UpdateApplicationStatus = z.infer<typeof UpdateApplicationStatusSchema>
export type ApplicationFilterParams = z.infer<typeof ApplicationFilterParamsSchema>
export type ApplicationStats = z.infer<typeof ApplicationStatsSchema>

// Document Schemas
export const DocumentSchema = z.object({
  documentId: z.string(),
  title: z.string(),
  documentUrl: z.string(),
  description: z.string().optional(),
  documentCategory: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  datePublished: z.string().nullable().optional(),
  publishedBy: z.string().nullable().optional(),
  publishedByName: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  userId: z.string().optional(),
  username: z.string().optional()
})

export const CreateDocumentSchema = z.object({
  title: z.string().min(1),
  documentCategory: z.string().min(1),
  description: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  fileUrl: z.string().url()
})

export const UpdateDocumentSchema = z.object({
  title: z.string().min(1),
  documentCategory: z.string().min(1),
  description: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  fileUrl: z.string().url()
})

export const UpdateDocumentStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const DocumentFilterParamsSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  documentCategory: z.string().optional(),
  searchBy: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

export const DocumentStatsSchema = z.object({
      //  "comparisonWindowDays": 30,
      //   "totalDocuments": {
      //       "totalCount": 12,
      //       "last30DaysCount": 0,
      //       "previous30DaysCount": 0,
      //       "percentageChange": 0.0
      //   },
      //   "publishedDocuments": {
      //       "totalCount": 8,
      //       "last30DaysCount": 0,
      //       "previous30DaysCount": 0,
      //       "percentageChange": 0.0
      //   },
      //   "draftDocuments": {
      //       "totalCount": 4,
      //       "last30DaysCount": 0,
      //       "previous30DaysCount": 0,
      //       "percentageChange": 0.0
      //   },
      //   "totalDownloads": 1,
      //   "latestUpdatedAt": "2025-12-09T21:29:05"
      comparisonWindowDays: z.number().default(30),
  totalDocuments: z.object({
    totalCount: z.number().default(0),
    last30DaysCount: z.number().default(0),
    previous30DaysCount: z.number().default(0),
    percentageChange: z.number().default(0)
  }),
  publishedDocuments: z.object({
    totalCount: z.number().default(0),
    last30DaysCount: z.number().default(0),
    previous30DaysCount: z.number().default(0),
    percentageChange: z.number().default(0)
  }),
  draftDocuments: z.object({
    totalCount: z.number().default(0),
    last30DaysCount: z.number().default(0),
    previous30DaysCount: z.number().default(0),
    percentageChange: z.number().default(0)
  }),
  totalDownloads: z.number().default(0),
  latestUpdatedAt: z.string().nullable()
})

export type Document = z.infer<typeof DocumentSchema>
export type CreateDocument = z.infer<typeof CreateDocumentSchema>
export type UpdateDocument = z.infer<typeof UpdateDocumentSchema>
export type UpdateDocumentStatus = z.infer<typeof UpdateDocumentStatusSchema>
export type DocumentFilterParams = z.infer<typeof DocumentFilterParamsSchema>
export type DocumentStats = z.infer<typeof DocumentStatsSchema>

// Announcement Schemas
export const AnnouncementSchema = z.object({
  announcementId: z.string(),
  imageUrl: z.string(),
  title: z.string(),
  description: z.string(),
  contentCategory: z.string(),
  announcementType: z.enum(['LINKED', 'MANUAL']),
  highlighted: z.boolean(),
  dateHighlighted: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  linkedContentId: z.string().nullable(),
  linkedContent: z.object({
    contentId: z.string(),
    contentType: z.string(),
    title: z.string(),
    imageUrl: z.string().nullable(),
  }).nullable()
})

export const AnnouncementDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  imageUrl: z.string(),
  title: z.string(),
  description: z.string(),
  contentCategory: z.string(),
  highlighted: z.boolean(),
  dateHighlighted: z.string().nullable()
})

export const ContentCategorySchema = z.object({
  value: z.string(),
  displayName: z.string()
})

export const CreateAnnouncementSchema = z.object({
  imageUrl: z.string().url(),
  title: z.string().min(1),
  description: z.string().min(1),
  contentCategory: z.string(),
  highlight: z.boolean().default(false),
  announcementType: z.enum(['LINKED', 'MANUAL']).default('LINKED'),
  // if announcementType is LINKED, a required linkedContentId field will be required in the request body, otherwise it will be ignored
  linkedContentId: z.string().optional().nullable()
}).superRefine((data, ctx) => {  if (data.announcementType === 'LINKED' && !data.linkedContentId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'linkedContentId is required when announcementType is LINKED',
      path: ['linkedContentId']
    });
  }
});



export const UpdateAnnouncementSchema = z.object({
  imageUrl: z.string().url().optional(),
  title: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  contentCategory: z.string().optional(),
  highlight: z.boolean().optional(),
  announcementType: z.enum(['LINKED', 'MANUAL']),
  linkedContentId: z.string().optional().nullable()
}).superRefine((data, ctx) => {
  if (data.announcementType === 'LINKED' && !data.linkedContentId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'linkedContentId is required when announcementType is LINKED',
      path: ['linkedContentId']
    });
  }
});

export const AnnouncementFilterParamsSchema = z.object({
  highlighted: z.boolean().optional(),
  contentCategory: z.string().optional()
})

export type Announcement = z.infer<typeof AnnouncementSchema>
export type AnnouncementDetail = z.infer<typeof AnnouncementDetailSchema>
export type ContentCategory = z.infer<typeof ContentCategorySchema>
export type CreateAnnouncement = z.infer<typeof CreateAnnouncementSchema>
export type UpdateAnnouncement = z.infer<typeof UpdateAnnouncementSchema>
export type AnnouncementFilterParams = z.infer<typeof AnnouncementFilterParamsSchema>

// Subscriber Schemas
export const SubscriberSchema = z.object({
  subscriberId: z.string(),
  email: z.string().email(),
  isActive: z.boolean(),
  dateJoined: z.string()
})

export const SubscriberFilterParamsSchema = z.object({
  isActive: z.boolean().optional()
})

export const SubscriberStatsMetricSchema = z.object({
  totalCount: z.number().default(0),
  last30DaysCount: z.number().default(0),
  previous30DaysCount: z.number().default(0),
  percentageChange: z.number().default(0)
})

export const SubscriberStatsSchema = z.object({  
activePercentageOfTotal: z.number().default(0),
activeSubscribers: z.number().default(0),
inactivePercentageOfTotal: z.number().default(0),
inactiveSubscribers: z.number().default(0),
newThisMonthPercentageChange: z.number().default(0),
newThisMonthSubscribers: z.number().default(0),
totalSubscribers: z.number().default(0)
})

export type Subscriber = z.infer<typeof SubscriberSchema>
export type SubscriberFilterParams = z.infer<typeof SubscriberFilterParamsSchema>
export type SubscriberStatsMetric = z.infer<typeof SubscriberStatsMetricSchema>
export type SubscriberStats = z.infer<typeof SubscriberStatsSchema>

// Update Admin User Request Type
export type UpdateAdminUserRequest = z.infer<typeof UpdateAdminUserRequestSchema>